import { useVariable } from '@softwareimaging/backstage';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ApiFetcherArgs, initClient, tsRestFetchApi } from '@ts-rest/core';
import { FC, PropsWithChildren, useMemo } from 'react';
import { useRequestToken } from '../authentication/hooks/auth';
import { Variables } from '../backstage/config';
import contracts from './contracts';
import { ClientContext } from './useAPIProvider';

export const APIProvider: FC<PropsWithChildren> = ({ children }) => {
  const apiBaseUrl = useVariable<Variables>('apiBaseUrl');
  if (!apiBaseUrl) throw new Error('Missing apiBaseUrl');
  const requestToken = useRequestToken();

  const client = useMemo(() => {
    return initClient(contracts, {
      baseUrl: apiBaseUrl,
      baseHeaders: {},
      api: async (args: ApiFetcherArgs) => {
        const headers = {
          ...args.headers
        };

        if (args.body instanceof Object) {
          headers['Content-Type'] = 'application/json';
        }

        try {
          const { accessToken } = await requestToken();
          if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
          }
        } catch (e) {
          console.warn(e);
          console.warn(`Access to get token failed but could be anonymous.`);
        }

        return tsRestFetchApi({
          ...args,
          headers
        });
      }
    });
  }, [apiBaseUrl, requestToken]);

  const queryClient = useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          suspense: true,
          retry: 1,
          staleTime: 1000 * 60 * 5,
          cacheTime: 1000 * 60 * 5,
          meta: {
            client
          }
        }
      }
    });
  }, [client]);

  return (
    <ClientContext.Provider value={client}>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        {children}
      </QueryClientProvider>
    </ClientContext.Provider>
  );
};
